import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Send, LockOpen } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const formatMoney = (n) =>
    Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFecha = (iso) => iso
    ? new Date(iso).toLocaleString('es-VE', { timeZone: 'America/Caracas', dateStyle: 'medium', timeStyle: 'short' })
    : '';

/**
 * Ultimo tramo del cierre: resumen por categoria, facturas (la diferencia contra
 * lo cobrado debe ser el IVA), lista de correos y el boton que sella y notifica.
 * Con soloLectura (venta cerrada) no se edita, ni se sella ni se reabre.
 */
const CierreFinal = ({ ventaId, venta, resumen, filas, onData, soloLectura = false }) => {
    const sellada = venta.cerrada;
    const cerrada = sellada || soloLectura;
    const [facturas, setFacturas] = useState([]);
    const [correos, setCorreos] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [cerrando, setCerrando] = useState(false);
    const [error, onError] = useState('');

    useEffect(() => {
        setFacturas((venta.facturas || []).map(f => ({ numero: f.numero, monto: String(f.monto) })));
        setCorreos((venta.destinatarios || []).join('\n'));
    }, [venta.facturas, venta.destinatarios]);

    const facturasLimpias = facturas
        .filter(f => f.numero.trim() || f.monto.trim())
        .map(f => ({ numero: f.numero.trim(), monto: f.monto.replace(',', '.') }));
    const correosLimpios = correos.split(/[\n,;]+/).map(c => c.trim()).filter(Boolean);
    const hayCambios =
        JSON.stringify(facturasLimpias.map(f => [f.numero, Number(f.monto)])) !== JSON.stringify((venta.facturas || []).map(f => [f.numero, Number(f.monto)]))
        || JSON.stringify(correosLimpios.map(c => c.toLowerCase())) !== JSON.stringify(venta.destinatarios || []);

    const guardarConfig = async () => {
        setGuardando(true);
        onError('');
        try {
            const r = await axiosInstance.put(`/api/ventas-especiales/${ventaId}/cierre/config`, {
                facturas: facturasLimpias, destinatarios: correosLimpios,
            });
            onData(r.data);
            return true;
        } catch (e) {
            onError(e.response?.data?.error || 'No se pudieron guardar las facturas y los correos');
            return false;
        } finally {
            setGuardando(false);
        }
    };

    const pendientes = filas.filter(f => !f.cierreCategoria || f.cierreCategoria === 'sin_conciliar').length;
    const porGH = filas.filter(f => f.cierreCategoria === 'liquidacion_pendiente').length;

    const cerrar = async () => {
        if (hayCambios && !(await guardarConfig())) return;
        const avisos = [];
        if (pendientes) avisos.push(`${pendientes} pedido(s) sin conciliar o sin clasificar`);
        if (porGH) avisos.push(`${porGH} liquidación(es) que GH no ha confirmado`);
        if (resumen.facturasCuadran === false) avisos.push('las facturas no cuadran con el IVA');
        const texto = `Se enviará el Excel de cierre a ${correosLimpios.length} correo(s) y la pestaña quedará en solo lectura.`
            + (avisos.length ? `\n\nOjo, todavía hay: ${avisos.join('; ')}. Irán señalados en el Excel.` : '')
            + '\n\n¿Cerrar y notificar?';
        if (!window.confirm(texto)) return;
        setCerrando(true);
        onError('');
        try {
            // Sin timeout: el correo sale a cada destinatario con el adjunto.
            const r = await axiosInstance.post(`/api/ventas-especiales/${ventaId}/cierre/cerrar`, {}, { timeout: 0 });
            onData(r.data);
        } catch (e) {
            onError(e.response?.data?.error || 'No se pudo cerrar la venta');
        } finally {
            setCerrando(false);
        }
    };

    const reabrir = async () => {
        if (!window.confirm('¿Reabrir el cierre? Podrás corregir y volver a cerrar; al cerrar de nuevo se envía otro correo.')) return;
        try {
            const r = await axiosInstance.post(`/api/ventas-especiales/${ventaId}/cierre/reabrir`);
            onData(r.data);
        } catch (e) {
            onError(e.response?.data?.error || 'No se pudo reabrir el cierre');
        }
    };

    const setFactura = (i, campo, valor) => setFacturas(prev => prev.map((f, j) => j === i ? { ...f, [campo]: valor } : f));

    return (
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Resumen</h3>
                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">
                            <tr>
                                <th className="text-left px-4 py-2.5">Categoría</th>
                                <th className="text-right px-3 py-2.5">Pagado (Bs)</th>
                                <th className="text-right px-3 py-2.5">A pagar (Bs)</th>
                                <th className="text-right px-4 py-2.5">Diferencia</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 tabular-nums">
                            {resumen.categorias.map(c => (
                                <React.Fragment key={c.key || 'sin'}>
                                    <tr>
                                        <td className="px-4 py-2 font-medium text-gray-900">{c.label} <span className="font-normal text-gray-400">{c.pedidos}</span></td>
                                        <td className="px-3 py-2 text-right">{formatMoney(c.pagado)}</td>
                                        <td className="px-3 py-2 text-right">{formatMoney(c.aPagar)}</td>
                                        <td className={`px-4 py-2 text-right ${c.diferencia ? 'text-red-700' : 'text-gray-400'}`}>{formatMoney(c.diferencia)}</td>
                                    </tr>
                                    {c.empleados.map(e => (
                                        <tr key={c.key + e.nombre} className="text-xs text-gray-500">
                                            <td className="pl-8 pr-4 py-1.5">{e.nombre}</td>
                                            <td className="px-3 py-1.5 text-right">{formatMoney(e.pagado)}</td>
                                            <td className="px-3 py-1.5 text-right">{formatMoney(e.aPagar)}</td>
                                            <td className="px-4 py-1.5 text-right">{formatMoney(e.diferencia)}</td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                            <tr className="bg-gray-50 font-semibold text-gray-900">
                                <td className="px-4 py-2.5">Total general</td>
                                <td className="px-3 py-2.5 text-right">{formatMoney(resumen.totalPagado)}</td>
                                <td className="px-3 py-2.5 text-right">{formatMoney(resumen.totalAPagar)}</td>
                                <td className="px-4 py-2.5 text-right">{formatMoney(resumen.totalDiferencia)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <div className="space-y-6">
                <section>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Facturas</h3>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                        {facturas.length === 0 && cerrada && <p className="text-sm text-gray-500">No se cargaron facturas.</p>}
                        <div className="space-y-2">
                            {facturas.map((f, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <input value={f.numero} disabled={cerrada} onChange={(e) => setFactura(i, 'numero', e.target.value)}
                                        placeholder="Número" aria-label={`Número de la factura ${i + 1}`}
                                        className="w-32 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50" />
                                    <input value={f.monto} disabled={cerrada} onChange={(e) => setFactura(i, 'monto', e.target.value)}
                                        placeholder="Monto con IVA (Bs)" inputMode="decimal" aria-label={`Monto de la factura ${i + 1}`}
                                        className="flex-1 min-w-0 text-sm text-right tabular-nums border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50" />
                                    {!cerrada && (
                                        <button onClick={() => setFacturas(prev => prev.filter((_, j) => j !== i))} className="p-1.5 text-gray-400 hover:text-red-600" aria-label={`Quitar factura ${i + 1}`}>
                                            <Trash2 size={15} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {!cerrada && (
                            <button onClick={() => setFacturas(prev => [...prev, { numero: '', monto: '' }])} className="mt-3 inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-800">
                                <Plus size={15} /> Agregar factura
                            </button>
                        )}
                        {resumen.facturasCuadran !== null && !hayCambios && (
                            <dl className="mt-4 pt-3 border-t border-gray-100 text-sm tabular-nums space-y-1">
                                <div className="flex justify-between"><dt className="text-gray-500">Total facturado</dt><dd>{formatMoney(resumen.totalFacturas)}</dd></div>
                                <div className="flex justify-between"><dt className="text-gray-500">Menos total a pagar</dt><dd>{formatMoney(resumen.diferenciaIva)}</dd></div>
                                <div className="flex justify-between"><dt className="text-gray-500">IVA esperado (16%)</dt><dd>{formatMoney(resumen.ivaEsperado)}</dd></div>
                                <div className={`flex justify-between font-medium ${resumen.facturasCuadran ? 'text-green-700' : 'text-red-700'}`}>
                                    <dt>{resumen.facturasCuadran ? 'Cuadra: la diferencia es el IVA' : 'No cuadra con el IVA'}</dt>
                                    <dd>{formatMoney(resumen.diferenciaIva - resumen.ivaEsperado)}</dd>
                                </div>
                            </dl>
                        )}
                    </div>
                </section>

                <section>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Correo de cierre</h3>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <label htmlFor="cierre-correos" className="block text-sm text-gray-600 mb-1">
                            A quién se le envía el Excel de cierre, un correo por línea
                        </label>
                        <textarea id="cierre-correos" value={correos} disabled={cerrada} onChange={(e) => setCorreos(e.target.value)} rows={5}
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50" />

                        {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}

                        {cerrada ? (
                            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                                {sellada ? (
                                    <p className="text-sm text-green-800">Cerrada por {venta.cierrePor || 'Costos'} el {formatFecha(venta.cierreAt)}.</p>
                                ) : (
                                    <p className="text-sm text-gray-500">El cierre de facturas no se selló antes de cerrar la venta.</p>
                                )}
                                {!soloLectura && (
                                    <button onClick={reabrir} className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                                        <LockOpen size={15} /> Reabrir cierre
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                                <p className="text-xs text-gray-500">
                                    {venta.reabiertoAt ? `Reabierta por ${venta.reabiertoPor || 'Costos'} el ${formatFecha(venta.reabiertoAt)}.` : ''}
                                </p>
                                <div className="flex gap-2">
                                    <button onClick={guardarConfig} disabled={!hayCambios || guardando}
                                        className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                                        {guardando ? 'Guardando' : 'Guardar'}
                                    </button>
                                    <button onClick={cerrar} disabled={cerrando || correosLimpios.length === 0}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                        {cerrando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Cerrar y notificar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default CierreFinal;
